import React from 'react';
import { Mic, Play, Youtube, PenLine, MessageSquare, Search } from 'lucide-react';

/**
 * HeroCollage - visual proof of "one recording, many outputs".
 * Static central mic + 5 floating deliverable tiles orbiting it.
 * All CSS-only, no libraries. Respects prefers-reduced-motion.
 */
export default function HeroCollage({ podcastCover }) {
  return (
    <div className="relative w-full h-full min-h-[420px] sm:min-h-[520px] lg:min-h-[600px]" aria-hidden="true" data-testid="hero-collage">
      {/* Radial glow behind */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[420px] h-[420px] rounded-full v2-blob"
          style={{
            background:
              'radial-gradient(circle at 40% 40%, rgba(188,81,111,0.45), rgba(167,70,138,0.25) 45%, rgba(167,70,138,0) 72%)',
            filter: 'blur(4px)',
          }}
        />
      </div>

      {/* Central mic disc */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
        <div
          className="w-32 h-32 sm:w-40 sm:h-40 rounded-full flex items-center justify-center shadow-[0_20px_60px_-10px_rgba(129,22,42,0.6)] hc-pulse"
          style={{
            background:
              'radial-gradient(circle at 30% 30%, #E0A8B8 0%, #BC516F 30%, #A7468A 65%, #81162A 100%)',
          }}
        >
          <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white/95 flex items-center justify-center">
            <Mic className="w-12 h-12 sm:w-16 sm:h-16 text-[#81162A]" strokeWidth={1.5} />
          </div>
        </div>
      </div>

      {/* Podcast cover tile - top left */}
      <div className="absolute tile-podcast hc-float">
        <div className="bg-white rounded-[14px] shadow-xl p-3 flex items-center gap-3 w-[220px] rotate-[-6deg]">
          {podcastCover ? (
            <img src={podcastCover} alt="Client podcast cover art, a show produced and published by Audio & Co. podcast management" title="Audio & Co. client podcast" width="56" height="56" className="w-14 h-14 rounded-[8px] object-cover flex-shrink-0" />
          ) : (
            <div className="w-14 h-14 rounded-[8px] bg-gradient-to-br from-[#BC516F] to-[#A7468A] flex items-center justify-center flex-shrink-0">
              <Mic className="w-6 h-6 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-[#BC516F]">Podcast</div>
            <div className="text-[13px] font-semibold text-[#1E2A44] leading-tight truncate">Episode 207 · Published</div>
            <div className="flex items-center gap-1 mt-1 text-[10px] text-[#64748B]">
              <Play className="w-2.5 h-2.5" fill="currentColor" />
              <span>42 min</span>
            </div>
          </div>
        </div>
      </div>

      {/* YouTube thumb tile - top right */}
      <div className="absolute tile-youtube hc-float">
        <div className="bg-white rounded-[14px] shadow-xl overflow-hidden w-[240px] rotate-[5deg]">
          <div className="relative aspect-video bg-gradient-to-br from-[#1E2A44] via-[#81162A] to-[#BC516F] flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center">
              <Play className="w-6 h-6 text-[#81162A]" fill="currentColor" />
            </div>
            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
              42:16
            </div>
          </div>
          <div className="p-3">
            <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#BC516F]">
              <Youtube className="w-3 h-3" />
              YouTube
            </div>
            <div className="text-[13px] font-semibold text-[#1E2A44] leading-tight mt-1 line-clamp-2">
              Full episode + optimised description
            </div>
          </div>
        </div>
      </div>

      {/* Vertical Short tile - middle right */}
      <div className="absolute tile-short hc-float">
        <div className="rotate-[8deg]">
          <div className="w-[120px] h-[200px] bg-[#1E2A44] rounded-[18px] shadow-xl p-1.5 border-2 border-[#0B0E1A]">
            <div className="w-full h-full rounded-[12px] bg-gradient-to-b from-[#BC516F] via-[#A7468A] to-[#81162A] relative overflow-hidden flex items-end p-2">
              <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5 text-white text-[9px] font-bold uppercase tracking-widest">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                Short
              </div>
              <div className="text-white text-[10px] font-semibold leading-tight">
                &ldquo;The one clip we recut daily&rdquo;
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog tile - bottom left */}
      <div className="absolute tile-blog hc-float">
        <div className="bg-white rounded-[14px] shadow-xl p-4 w-[240px] rotate-[3deg]">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#BC516F]">
            <PenLine className="w-3 h-3" />
            SEO blog post
          </div>
          <div className="text-[13px] font-semibold text-[#1E2A44] leading-tight mt-1.5">
            &ldquo;Turning one weekly recording into a compounding content engine&rdquo;
          </div>
          <div className="mt-2 flex gap-1">
            <span className="h-1 w-8 rounded-full bg-[#BC516F]" />
            <span className="h-1 w-4 rounded-full bg-[#E0A8B8]" />
            <span className="h-1 w-2 rounded-full bg-[#E0A8B8]" />
          </div>
          <div className="mt-2 h-1 rounded-full bg-[#E2E8F0]" />
          <div className="mt-1.5 h-1 rounded-full bg-[#E2E8F0] w-4/5" />
        </div>
      </div>

      {/* Community post tile - bottom center */}
      <div className="absolute tile-community hc-float">
        <div className="bg-white rounded-[14px] shadow-xl p-3 w-[180px] rotate-[-4deg]">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#BC516F]">
            <MessageSquare className="w-3 h-3" />
            Community
          </div>
          <div className="text-[12px] font-medium text-[#1E2A44] leading-tight mt-1">
            Daily post to keep the channel warm
          </div>
          <div className="mt-2 flex items-center gap-2 text-[10px] text-[#64748B]">
            <span>👍 128</span>
            <span>💬 24</span>
          </div>
        </div>
      </div>

      {/* Google SERP tile - middle left */}
      <div className="absolute tile-serp hc-float">
        <div className="bg-white rounded-[10px] shadow-xl p-3 w-[220px] rotate-[-3deg]">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#BC516F]">
            <Search className="w-3 h-3" />
            Ranking
          </div>
          <div className="text-[11px] text-[#1E7AC7] mt-1 truncate">audioandco.com › blog</div>
          <div className="text-[12px] font-semibold text-[#1E2A44] leading-tight mt-0.5">
            Best done-for-you podcast production 2026
          </div>
          <div className="text-[10px] text-[#64748B] leading-snug mt-0.5 line-clamp-2">
            Every week, one recording becomes twelve outputs across podcast, YouTube&hellip;
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hc-float {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50%      { transform: translateY(-10px) rotate(calc(var(--r, 0deg) + 1deg)); }
        }
        @keyframes hc-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
        .hc-float { animation: hc-float 6s ease-in-out infinite; will-change: transform; }
        .hc-pulse { animation: hc-pulse 4s ease-in-out infinite; will-change: transform; }
        .tile-podcast   { top: 8%;  left: 2%;  animation-delay: 0s; z-index: 4; }
        .tile-youtube   { top: 4%;  right: 4%; animation-delay: 1s; z-index: 4; }
        .tile-short     { top: 30%; right: 2%; animation-delay: 2s; z-index: 5; }
        .tile-blog      { bottom: 6%; left: 4%; animation-delay: 3s; z-index: 4; }
        .tile-community { bottom: 2%; left: 46%; animation-delay: 4s; z-index: 5; }
        .tile-serp      { top: 44%; left: 0%; animation-delay: 2.5s; z-index: 3; }
        @media (prefers-reduced-motion: reduce) {
          .hc-float, .hc-pulse { animation: none !important; }
        }
        @media (max-width: 640px) {
          .tile-podcast, .tile-youtube, .tile-blog, .tile-community, .tile-short, .tile-serp {
            transform: scale(0.7);
            transform-origin: center;
          }
        }
      `}</style>
    </div>
  );
}
